import React, { useEffect, useState } from 'react'
import { browserHistory } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Button, Result, Skeleton, Tabs } from 'antd'
import { CalendarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import {
  accessControlApi,
  type PermissionDto,
} from '../../api/access-control.client'
import {
  roleDetailQueryKey,
  useRoleDetailQuery,
} from '../../hooks/useRoleDetailQuery'
import { useRoleUsersQuery } from '../../hooks/useRoleUsersQuery'
import { useSubjectsQuery } from '../../hooks/useSubjectsQuery'
import { useCanManagePermissions } from '../../hooks/useCanManagePermissions'
import {
  isFixedPurposeRole,
  isLockedRole,
  isSubjectGrantableToRole,
} from '../../locked-roles'
import { MY_ABILITY_QUERY_KEY } from '../../../../rbac/useMyAbilityQuery'
import PermissionsEditor from '../../components/PermissionsEditor'
import RoleUsersTab from '../../components/RoleUsersTab'
import StatusBadge from '../../components/StatusBadge'
import AuditHistory from '../Audit/AuditLog'
import { notifyError, notifySuccess } from '../../utils/notify'
import { ACCESS_CONTROL } from '../../paths'
import styles from './RoleDetail.module.scss'

interface Props {
  params: { roleId: string }
}

function canonical(permissions: PermissionDto[]): string {
  // A subject with no actions selected is not an actual grant — it is never
  // persisted, so it must not count toward dirty state either. Ignoring empties
  // keeps "added a menu but picked nothing" from showing phantom unsaved changes.
  return permissions
    .filter((p) => p.actions.length > 0)
    .map((p) => `${p.subjectId}:${[...p.actions].sort().join(',')}`)
    .sort()
    .join('|')
}

function readOnlyReason(
  roleName: string | undefined,
  isLocked: boolean
): 'locked' | 'fixed' | 'viewOnly' {
  if (isFixedPurposeRole(roleName)) return 'fixed'
  return isLocked ? 'locked' : 'viewOnly'
}

export default function RoleDetail({ params }: Props) {
  const { roleId } = params
  const queryClient = useQueryClient()
  const { data: role, isLoading, isError } = useRoleDetailQuery(roleId)
  const { data: roleUsers = [] } = useRoleUsersQuery(roleId)
  const { data: subjects = [], isLoading: subjectsLoading } = useSubjectsQuery()
  const [localPermissions, setLocalPermissions] = useState<
    PermissionDto[] | null
  >(null)

  useEffect(() => {
    setLocalPermissions(null)
  }, [roleId])

  const savedPermissions = role?.permissions ?? []
  const permissions = localPermissions ?? savedPermissions
  // 'Platform Admin', 'RBAC Admin' and 'US-Restricted Data Access' are locked —
  // their permissions cannot be edited (the EPS upsert rejects them with a 422;
  // this disables the UI to match).
  const isLocked = isLockedRole(role?.name)
  // Role-exclusive subjects (UsRestrictedData) are only offered on their own role.
  const grantableSubjects = subjects.filter((s) =>
    isSubjectGrantableToRole(s.subjectId, role?.name)
  )
  const canManage = useCanManagePermissions()
  const isReadOnly = isLocked || !canManage
  const isDirty =
    !isReadOnly &&
    localPermissions !== null &&
    canonical(localPermissions) !== canonical(savedPermissions)

  const { mutate: savePermissions, isPending } = useMutation({
    mutationFn: (perms: PermissionDto[]) =>
      accessControlApi.upsertRolePermissions(
        roleId,
        perms.filter((p) => p.actions.length > 0)
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleDetailQueryKey(roleId) })
      // Editing this role's permissions changes what every holder can do,
      // including possibly the current user — bust their ability cache so menus
      // and route gates reflect it now rather than after the ~5-min staleTime.
      queryClient.invalidateQueries({ queryKey: MY_ABILITY_QUERY_KEY })
      setLocalPermissions(null)
      notifySuccess('Permissions saved', 'Role permissions updated.')
    },
    onError: () =>
      notifyError('Save failed', 'Could not save permissions. Try again.'),
  })

  const tabs = [
    {
      key: 'permissions',
      label: 'Permissions',
      children: (
        <div className={styles.permissionsTab}>
          {isLoading || subjectsLoading ? (
            // Don't render the editor until the role's saved permissions AND
            // the subject catalog have loaded. Editing against the empty `[]`
            // placeholder would let a save replace the role's real grants with
            // a partial/empty set; an empty catalog would hide action pills.
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <PermissionsEditor
              permissions={permissions}
              subjects={grantableSubjects}
              onChange={(updated) => setLocalPermissions(updated)}
              readOnly={isReadOnly}
              readOnlyReason={readOnlyReason(role?.name, isLocked)}
            />
          )}
        </div>
      ),
    },
    {
      key: 'users',
      label: (
        <span>
          Users
          {roleUsers.length > 0 && (
            <span className={styles.countBadge}>{roleUsers.length}</span>
          )}
        </span>
      ),
      children: <RoleUsersTab roleId={roleId} />,
    },
    {
      key: 'audit',
      label: 'Audit History',
      children: <AuditHistory targetId={roleId} />,
    },
  ]

  // The role failed to load (deleted, bad id, or the request errored). Show an
  // inline not-found state instead of an empty editor that would save against a
  // role that does not exist.
  const notFound = !isLoading && (isError || !role)

  if (notFound) {
    return (
      <PageWrapper>
        <Page
          title={<span className={styles.pageTitle}>Role Detail</span>}
          breadcrumb={
            <BreadcrumbNav
              items={[
                { title: 'Permission Management', path: ACCESS_CONTROL },
                { title: 'Not found' },
              ]}
            />
          }
        >
          <div className={styles.container}>
            <Result
              status="404"
              title="Role not found"
              subTitle="This role may have been deleted or the link is no longer valid."
              extra={
                <Button
                  type="primary"
                  onClick={() => browserHistory.push(ACCESS_CONTROL)}
                >
                  Back to Permission Management
                </Button>
              }
            />
          </div>
        </Page>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Page
        title={
          <span className={styles.pageTitle}>
            {role?.name ?? 'Role Detail'}
          </span>
        }
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Permission Management', path: ACCESS_CONTROL },
              { title: role?.name ?? '...' },
            ]}
          />
        }
      >
        <div className={styles.container}>
          {isLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div className={styles.identityCard}>
              <div className={styles.nameRow}>
                <span className={styles.roleName}>{role?.name}</span>
                <StatusBadge status={role?.status ?? ''} />
              </div>
              <div className={styles.meta}>
                <CalendarOutlined />
                Created{' '}
                {role?.createdAt
                  ? dayjs(role.createdAt).format('MMMM D, YYYY')
                  : '—'}
              </div>
              {role?.description && (
                <p className={styles.description}>{role.description}</p>
              )}
            </div>
          )}

          <Tabs
            className={styles.tabs}
            items={tabs}
            defaultActiveKey="permissions"
          />
        </div>

        {isDirty && (
          <div className={styles.saveBar}>
            <div className={styles.saveBarInner}>
              <span className={styles.unsaved}>
                <span className={styles.unsavedDot} />
                You have unsaved changes
              </span>
              <div className={styles.saveActions}>
                <Button onClick={() => setLocalPermissions(null)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  loading={isPending}
                  onClick={() => savePermissions(permissions)}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </Page>
    </PageWrapper>
  )
}
